import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LaunchIcon from '@mui/icons-material/Launch';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import TranslateIcon from '@mui/icons-material/Translate';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  Table, TableBody, TableCell,
  TableContainer, TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Paper,
  Select,
  useTheme
} from '@mui/material';
import Snackbar from '@mui/material/Snackbar';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import TableSortLabel from '@mui/material/TableSortLabel';
import { visuallyHidden } from '@mui/utils';
import React, { useEffect, useState } from 'react';
import Swal from 'sweetalert2';
import VideoProcessingService from '../../services/VideoCRUDService';
import { getChipColor, getChipLabel, getProcessStageLabel } from '../../utils/processStageUtils';
import AdminNavbar from './AdminNavbar';
import { formatDate } from '../../utils/formatUtils';
import AdminPageWrapper from './AdminPageWrapper';

const MongoVideosBrowser = ({ darkMode, toggleDarkMode, themeVariant }) => {
    const theme = useTheme();
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogAction, setDialogAction] = useState('');
  const [languageFilter, setLanguageFilter] = useState('all');
  const [selectedVideos, setSelectedVideos] = useState([]);
  
  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(12);

  // Snackbar state
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  // Sorting state
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('videoName');
// Add states for menu control
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuVideo, setMenuVideo] = useState(null);
  
  // Menu open/close handlers
  const handleMenuOpen = (event, video) => {
    setAnchorEl(event.currentTarget);
    setMenuVideo(video);
  };
  
  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuVideo(null);
  };

  useEffect(() => {
    fetchVideos();
  }, []);

  const fetchVideos = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Using API v2 for MongoDB data
      const response = await VideoProcessingService.getVideos('v2');
      
      // Check what is in the response and extract the videos array
      console.log('Response from getVideos:', response);
      
      // The data is likely in response.data, not directly in response
      const videosData = response.data ? response.data : (Array.isArray(response) ? response : []);
      
      // Sort videos by date (newest first)
      const sortedVideos = videosData.sort((a, b) => 
        new Date(b.updatedAt || b.createdAt) - new Date(a.updatedAt || a.createdAt)
      );
      
      setVideos(sortedVideos);
    } catch (err) {
      setError(`Error loading videos: ${err.message}`);
      console.error("Error fetching MongoDB videos:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    fetchVideos();
  };

  const handleLanguageFilterChange = (event) => {
    setLanguageFilter(event.target.value);
    setPage(0); // Reset pagination when changing filters
  };

  const handleSearch = () => {
    // Reset to first page when searching
    setPage(0);
  };

  const handleSearchKeyDown = (event) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  const openDialog = (action, video) => {
    setSelectedVideo(video);
    setDialogAction(action);
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setSelectedVideo(null);
  };

  const handleDelete = async () => {
    if (!selectedVideo) return;
    
    try {
      await VideoProcessingService.delete(
        selectedVideo.id);
      
      closeDialog();
      handleRefresh();
    } catch (err) {
      setError(`Error deleting video: ${err.message}`);
    }
  };

  const handleBatchDeleteVideos = async () => {
    if (selectedVideos.length === 0) return;
    
    Swal.fire({
      title: 'Delete Multiple Videos',
      text: `Are you sure you want to delete ${selectedVideos.length} videos? This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete them!'
    }).then(async (result) => {
      if (result.isConfirmed) {
        setLoading(true);
        let successCount = 0;
        let errorCount = 0;
        
        for (const video of selectedVideos) {
          try {
            await VideoProcessingService.delete(video.id);
            successCount++;
          } catch (err) {
            console.error(`Error deleting video ${video.id}:`, err);
            errorCount++;
          }
        }
        
        setSnackbarOpen(true);
        if (errorCount === 0) {
          setSnackbarMessage(`Successfully deleted ${successCount} videos`);
        } else {
          setSnackbarMessage(`Deleted ${successCount} videos, ${errorCount} failed`);
        }
        
        setSelectedVideos([]);
        setLoading(false);
        fetchVideos();
      }
    });
  };

  const handleVideoSelection = (video, isSelected) => {
    if (isSelected) {
      setSelectedVideos(prev => [...prev, video]);
    } else {
      setSelectedVideos(prev => prev.filter(v => v.id !== video.id));
    }
  };

  const handleCopyUrl = (url) => {
    navigator.clipboard.writeText(url)
      .then(() => {
        setSnackbarMessage('URL copied to clipboard');
        setSnackbarOpen(true);
      })
      .catch(err => {
        console.error('Error copying URL: ', err);
        setSnackbarMessage('Failed to copy URL');
        setSnackbarOpen(true);
      });
  };

  const handleClearVersionHistory = (videoId) => {
    Swal.fire({
      title: 'Clear Version History',
      text: 'Are you sure you want to delete all version history for this video? This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, clear it!'
    }).then((result) => {
      if (result.isConfirmed) {
        setLoading(true);
        // Call your API to clear version history
        VideoProcessingService.clearVersionHistory(videoId)
          .then(() => {
            setSnackbarMessage('Version history cleared successfully');
            setSnackbarOpen(true);
            setLoading(false);
          })
          .catch(err => {
            console.error('Error clearing version history:', err);
            setSnackbarMessage('Failed to clear version history');
            setSnackbarOpen(true);
            setLoading(false);
          });
      }
    });
    handleMenuClose();
  };

  const handleDeleteLanguages = (videoId) => {
    // First get available languages for this video
    const video = videos.find(v => v.id === videoId);
    if (!video || !video.videoLanguages || video.videoLanguages.length === 0) {
      setSnackbarMessage('No languages found for this video');
      setSnackbarOpen(true);
      handleMenuClose();
      return;
    }
    
    // Create checkboxes for each language
    const languageOptions = video.videoLanguages.map(lang => {
      return `<div style="margin: 8px 0;">
        <label style="display: flex; align-items: center;">
          <input type="checkbox" name="languages" value="${lang.languageName}" style="margin-right: 8px;"> 
          ${lang.languageName}
        </label>
      </div>`;
    }).join('');
    
    Swal.fire({
      title: 'Delete Languages',
      html: `
        <p>Select languages to delete:</p>
        <div style="text-align: left; margin: 16px 0;">
          ${languageOptions}
        </div>
      `,
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Delete Selected',
      preConfirm: () => {
        const selectedLanguages = Array.from(
          document.querySelectorAll('input[name="languages"]:checked')
        ).map(el => el.value);
        
        if (selectedLanguages.length === 0) {
          Swal.showValidationMessage('Please select at least one language');
          return false;
        }
        
        return selectedLanguages;
      }
    }).then((result) => {
      if (result.isConfirmed && result.value) {
        setLoading(true);
        // Call your API to delete selected languages
        VideoProcessingService.deleteLanguages(videoId, result.value)
          .then(() => {
            setSnackbarMessage(`Successfully deleted ${result.value.length} language(s)`);
            setSnackbarOpen(true);
            handleRefresh();
          })
          .catch(err => {
            console.error('Error deleting languages:', err);
            setSnackbarMessage('Failed to delete languages');
            setSnackbarOpen(true);
            setLoading(false);
          });
      }
    });
    
    handleMenuClose();
  };

  // Filter videos based on tab, search, and language filter
  const filteredVideos = videos.filter(video => {
    
    // Then filter by search term
    if (searchTerm && !video.name?.toLowerCase().includes(searchTerm.toLowerCase()) &&
        !video.videoName?.toLowerCase().includes(searchTerm.toLowerCase())) {
      return false;
    }
    
    // Then filter by language
    if (languageFilter !== 'all') {
      if (video.videoLanguages && video.videoLanguages.length > 0) {
        return video.videoLanguages.some(lang => 
          lang.languageName.toLowerCase() === languageFilter.toLowerCase());
      } 
      return false;
    }
    
    return true;
  });

  // Get available languages across all videos
  const availableLanguages = [...new Set(
    videos.flatMap(video => {
      if (video.videoLanguages && video.videoLanguages.length > 0) {
        return video.videoLanguages.map(lang => lang.languageName);
      } 
      return [];
    })
  )];
  // Sort available languages alphabetically
  // Handle pagination
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Sort function
  const sortedVideos = React.useMemo(() => {
    if (!filteredVideos) return [];
    
    return [...filteredVideos].sort((a, b) => {
      // Handle special properties
      if (orderBy === 'lastModifiedDate') {
        const dateA = a[orderBy] ? new Date(a[orderBy]).getTime() : 0;
        const dateB = b[orderBy] ? new Date(b[orderBy]).getTime() : 0;
        return order === 'asc' ? dateA - dateB : dateB - dateA;
      }
      
      // Handle languages (count number of languages)
      if (orderBy === 'videoLanguages') {
        const countA = a.videoLanguages ? a.videoLanguages.length : 0;
        const countB = b.videoLanguages ? b.videoLanguages.length : 0;
        return order === 'asc' ? countA - countB : countB - countA;
      }
      
      // Handle duration (convert to seconds)
      if (orderBy === 'duration') {
        const getDurationInSeconds = (duration) => {
          if (!duration) return 0;
          const parts = duration.split(':').map(Number);
          if (parts.length === 3) { // HH:MM:SS
            return parts[0] * 3600 + parts[1] * 60 + parts[2];
          } else if (parts.length === 2) { // MM:SS
            return parts[0] * 60 + parts[1];
          }
          return 0;
        };
        
        const durationA = getDurationInSeconds(a.duration);
        const durationB = getDurationInSeconds(b.duration);
        return order === 'asc' ? durationA - durationB : durationB - durationA;
      }
      
      // Default string comparison
      const valueA = a[orderBy] || '';
      const valueB = b[orderBy] || '';
      
      if (typeof valueA === 'string' && typeof valueB === 'string') {
        return order === 'asc' 
          ? valueA.localeCompare(valueB) 
          : valueB.localeCompare(valueA);
      }
      
      // Default numeric comparison
      return order === 'asc' 
        ? (valueA < valueB ? -1 : 1) 
        : (valueB < valueA ? -1 : 1);
    });
  }, [filteredVideos, order, orderBy]);

  const paginatedVideos = React.useMemo(() => {
    if (!sortedVideos) return [];
    const start = page * rowsPerPage;
    return sortedVideos.slice(start, start + rowsPerPage);
  }, [sortedVideos, page, rowsPerPage]);

  // Format file size
  const formatFileSize = (bytes) => {
    if (!bytes) return 'N/A';
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    if (bytes === 0) return '0 Byte';
    const i = parseInt(Math.floor(Math.log(bytes) / Math.log(1024)));
    return Math.round(bytes / Math.pow(1024, i), 2) + ' ' + sizes[i];
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  return (
    <AdminPageWrapper
      title="MongoDB Videos Browser"
      darkMode={darkMode}
      toggleDarkMode={toggleDarkMode}
      themeVariant={themeVariant}
    >
      <Box sx={{ 
        width: '100%', 
        p: { xs: 1, sm: 1, md: 2 },
        // Use theme based background instead of hardcoded colors
        backgroundColor: theme => theme.palette.mode === 'dark' 
          ? theme.palette.background.default 
          : theme.palette.background.subtle,
        minHeight: '100vh',
        maxWidth: '1600px',
        margin: '0 auto',
      }}>
        
        <Container sx={{ mt: 4, maxWidth: '100% !important', px: { xs: 1, sm: 2, md: 3 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Typography variant="h4">Videos Browser</Typography>
            <Button 
              startIcon={<ArrowBackIcon />} 
              sx={{ ml: 'auto' }}
              onClick={() => window.history.back()}
            >
              Back to Dashboard
            </Button>
          </Box>

          <Paper sx={{ p: 3, mb: 3, overflow: 'hidden' }}>
            <Box sx={{ 
              display: 'flex', 
              flexWrap: 'wrap', 
              gap: 2, 
              alignItems: 'center', 
              mb: 3,
              justifyContent: 'space-between'
            }}>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                <TextField 
                  size="small"
                  placeholder="Search videos..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={handleSearchKeyDown}
                />
                
                <Button 
                  variant="contained"
                  startIcon={<SearchIcon />}
                  onClick={handleSearch}
                >
                  Search
                </Button>
                
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>Language Filter</InputLabel>
                  <Select
                    value={languageFilter}
                    label="Language Filter"
                    onChange={handleLanguageFilterChange}
                  >
                    <MenuItem value="all">All Languages</MenuItem>
                    {availableLanguages.map(lang => (
                      <MenuItem key={lang} value={lang}>{lang}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                {/* Add batch delete button here */}
                {selectedVideos.length > 0 && (
                  <Button
                    variant="contained"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={handleBatchDeleteVideos}
                  >
                    Delete {selectedVideos.length} Selected
                  </Button>
                )}
                
                <Button 
                  variant="contained" 
                  color="success" 
                  startIcon={<RefreshIcon />}
                  onClick={handleRefresh}
                >
                  Refresh
                </Button>
              </Box>
            </Box>

            
            {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
            
            <Box sx={{ mb: 2 }}>
              <Chip 
                icon={<VideoLibraryIcon />} 
                label={`${filteredVideos.length} videos`} 
                color="secondary"
                variant="outlined"
                sx={{ mr: 1 }}
              />
              
            </Box>
            
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <TableContainer sx={{ overflow: 'auto', maxWidth: '100%' }}>
                  <Table sx={{ minWidth: 1200 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox">
                          <Checkbox 
                            checked={selectedVideos.length === videos.length}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedVideos([...videos]);
                              } else {
                                setSelectedVideos([]);
                              }
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <TableSortLabel
                            active={orderBy === 'videoName'}
                            direction={orderBy === 'videoName' ? order : 'asc'}
                            onClick={() => handleRequestSort('videoName')}
                          >
                            Name
                            {orderBy === 'videoName' ? (
                              <Box component="span" sx={visuallyHidden}>
                                {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                              </Box>
                            ) : null}
                          </TableSortLabel>
                        </TableCell>
                        <TableCell>
                          <TableSortLabel
                            active={orderBy === 'duration'}
                            direction={orderBy === 'duration' ? order : 'asc'}
                            onClick={() => handleRequestSort('duration')}
                          >
                            Duration
                            {orderBy === 'duration' ? (
                              <Box component="span" sx={visuallyHidden}>
                                {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                              </Box>
                            ) : null}
                          </TableSortLabel>
                        </TableCell>
                        <TableCell>
                          <TableSortLabel
                            active={orderBy === 'lastModifiedDate'}
                            direction={orderBy === 'lastModifiedDate' ? order : 'asc'}
                            onClick={() => handleRequestSort('lastModifiedDate')}
                          >
                            Last Modified
                            {orderBy === 'lastModifiedDate' ? (
                              <Box component="span" sx={visuallyHidden}>
                                {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                              </Box>
                            ) : null}
                          </TableSortLabel>
                        </TableCell>
                        <TableCell>
                          <TableSortLabel
                            active={orderBy === 'videoLanguages'}
                            direction={orderBy === 'videoLanguages' ? order : 'asc'}
                            onClick={() => handleRequestSort('videoLanguages')}
                          >
                            Languages
                            {orderBy === 'videoLanguages' ? (
                              <Box component="span" sx={visuallyHidden}>
                                {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                              </Box>
                            ) : null}
                          </TableSortLabel>
                        </TableCell>
                        <TableCell>GoogleDriveURL</TableCell>
                        <TableCell>CompressedURL</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paginatedVideos.length > 0 ? paginatedVideos.map((video) => (
                        <TableRow key={video.id}>
                          <TableCell padding="checkbox">
                            <Checkbox 
                              checked={selectedVideos.some(v => v.id === video.id)}
                              onChange={(e) => handleVideoSelection(video, e.target.checked)}
                            />
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <VideoLibraryIcon color="secondary" fontSize="small" />
                              <Typography variant="body2" sx={{ 
                                maxWidth: 250,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}>
                                {video.videoName}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>{video.duration}</TableCell>
                          <TableCell>
                            {formatDate(video.lastModifiedDate)}
                          </TableCell>

                          <TableCell>
                          {
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {video.videoLanguages.map((lang, index) => (
                                <Tooltip 
                                      key={lang.name}
                                      title={`${lang.name} (${getProcessStageLabel(lang.processStage)})`}
                                      arrow
                                  >
                                  <Chip 
                                  key={index}
                                  label= {getChipLabel(lang.languageName, lang.processStage)}
                                  size="small"
                                  sx={{ 
                                    fontSize: '0.7rem',
                                    backgroundColor: `${getChipColor(lang.processStage, theme.palette.mode)}20`,
                                    color: getChipColor(lang.processStage, theme.palette.mode),
                                    borderColor: getChipColor(lang.processStage, theme.palette.mode)
                                  }}
                                  variant="outlined"
                                  
                                  />
                                  </Tooltip>
                              ))}
                              </Box>
                          }
                          </TableCell>
                          <TableCell>
                            {video.videoUrl ? (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Tooltip title="Copy URL to clipboard">
                                  <IconButton size="small" onClick={() => handleCopyUrl(video.videoUrl)}>
                                    <ContentCopyIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Open URL in new tab">
                                  <IconButton size="small" onClick={() => window.open(video.videoUrl, '_blank')}>
                                    <LaunchIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            ) : (
                              <Typography variant="body2" color="text.secondary">No URL</Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            {video.compressedUrl ? (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Tooltip title="Copy URL to clipboard">
                                  <IconButton size="small" onClick={() => handleCopyUrl(video.compressedUrl)}>
                                    <ContentCopyIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Open URL in new tab">
                                  <IconButton size="small" onClick={() => window.open(video.compressedUrl, '_blank')}>
                                    <LaunchIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            ) : (
                              <Typography variant="body2" color="text.secondary">No URL</Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <IconButton 
                              size="small"
                              onClick={(event) => handleMenuOpen(event, video)}
                              aria-label="actions"
                            >
                              <MoreVertIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      )) : (
                        <TableRow>
                          <TableCell colSpan={7} align="center">
                            No videos found matching your criteria
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                
                <TablePagination
                  component="div"
                  count={filteredVideos.length}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[10, 25, 50]}
                />
              </>
            )}
          </Paper>
          
          {selectedVideo && (
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Video Details: {selectedVideo.name}
              </Typography>
              
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography>Metadata & Properties</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <pre style={{ overflow: 'auto', maxHeight: '400px' }}>
                    {JSON.stringify(selectedVideo, null, 2)}
                  </pre>
                </AccordionDetails>
              </Accordion>
            </Paper>
          )}
        
        {/* Delete Confirmation Dialog */}
        <Dialog open={dialogOpen && dialogAction === 'delete'} onClose={closeDialog}>
          <DialogTitle>Delete Video</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete "{selectedVideo?.name}"? This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeDialog}>Cancel</Button>
            <Button 
              onClick={handleDelete}
              color="error"
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
        
        {/* Edit Video Dialog - would be implemented with full form controls */}
        <Dialog 
          open={dialogOpen && dialogAction === 'edit'} 
          onClose={closeDialog}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Edit Video: {selectedVideo?.name}</DialogTitle>
          <DialogContent>
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              Video editing functionality would be implemented here with form controls for all editable properties.
            </Typography>
            <Alert severity="info">
              This is a placeholder for the video editing interface. In a real implementation, 
              you would have form fields for video properties, subtitle management, etc.
            </Alert>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeDialog}>Close</Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbarOpen}
          autoHideDuration={3000}
          onClose={() => setSnackbarOpen(false)}
          message={snackbarMessage}
        />

        {/* Action Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          <MenuItem onClick={() => {
            if (menuVideo && menuVideo.compressedUrl) window.open(menuVideo.compressedUrl, '_blank');
            handleMenuClose();
          }}>
            <ListItemIcon>
              <PlayArrowIcon fontSize="small" color="info" />
            </ListItemIcon>
            <ListItemText>Play Video</ListItemText>
          </MenuItem>
          
          <MenuItem onClick={() => {
            if (menuVideo) openDialog('edit', menuVideo);
            handleMenuClose();
          }}>
            <ListItemIcon>
              <EditIcon fontSize="small" color="primary" />
            </ListItemIcon>
            <ListItemText>Edit Video</ListItemText>
          </MenuItem>
          
          <MenuItem onClick={() => {
            if (menuVideo) handleClearVersionHistory(menuVideo.id);
          }}>
            <ListItemIcon>
              <DeleteSweepIcon fontSize="small" color="warning" />
            </ListItemIcon>
            <ListItemText>Clear Version History</ListItemText>
          </MenuItem>
          
          <MenuItem onClick={() => {
            if (menuVideo) handleDeleteLanguages(menuVideo.id);
          }}>
            <ListItemIcon>
              <TranslateIcon fontSize="small" color="secondary" />
            </ListItemIcon>
            <ListItemText>Delete Languages</ListItemText>
          </MenuItem>
          
          <MenuItem onClick={() => {
            if (menuVideo) openDialog('delete', menuVideo);
            handleMenuClose();
          }}>
            <ListItemIcon>
              <DeleteIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText>Delete Video</ListItemText>
          </MenuItem>
        </Menu>
      </Container>
      </Box>
    </AdminPageWrapper>
  );
};

export default MongoVideosBrowser;